import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useThemeClasses } from "../../providers/ThemeProvider";
import { useWhatsNewBriefing } from "../../hooks/useWhatsNewBriefing";
import { extractMarkup } from "../../services/WhatsNewService";
import { useSettingsStore } from "../../store/SettingsStore";
import SandboxedHtmlFrame from "./whatsNew/SandboxedHtmlFrame";
import { cx } from "@digitalisation/enlixy-comp";

function formatDay(d: Date): string {
  return d.toLocaleDateString();
}

const Skeleton = () => (
  <div className="space-y-3 animate-pulse" aria-hidden="true">
    <div className="h-4 rounded w-2/5 bg-gray-300/40" />
    <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="h-20 rounded-lg bg-gray-300/40" />
      ))}
    </div>
    <div className="h-3 rounded w-4/5 bg-gray-300/40" />
    <div className="h-3 rounded w-3/5 bg-gray-300/40" />
  </div>
);

/**
 * "What's New" — a personalised briefing of what changed on the user's machine
 * park since the last time they were briefed.
 *
 * The body is HTML written by the AI agent, so it is rendered inside
 * `SandboxedHtmlFrame` (an isolated iframe) and never injected into this
 * document. Machine references are returned separately and rendered here as real
 * router links, because the sandbox intentionally blocks navigation from inside.
 */
const WelcomeTab = () => {
  const { t } = useTranslation();
  const themeClasses = useThemeClasses();
  const demoMode = useSettingsStore((s) => s.demoMode);
  const {
    status,
    summary,
    generatedAt,
    windowStart,
    error,
    regenerate,
    machineCount,
  } = useWhatsNewBriefing();

  const busy = status === "loading" || status === "generating";

  // i18next returns the key itself when an entry is missing, so the common
  // `t(...) || fallback` idiom never actually falls back. `defaultValue` does.
  const subtitle = (() => {
    if (status === "generating") {
      return t("whatsNew.generating", {
        defaultValue: "Reviewing your machines since your last visit…",
      });
    }
    if (windowStart && generatedAt) {
      return t("whatsNew.window", {
        from: formatDay(windowStart),
        to: formatDay(generatedAt),
        defaultValue: "Changes from {{from}} to {{to}}",
      });
    }
    if (machineCount > 0) {
      return t("whatsNew.machineCount", {
        count: machineCount,
        defaultValue: "{{count}} machines in your park",
      });
    }
    return null;
  })();

  const title = t("whatsNew.title", {
    defaultValue: "What's new since your last visit",
  });

  /**
   * Strip the agent's narration at render, not only at generation.
   *
   * Doing it only on the way in meant anything already cached — written before
   * that trimming existed — kept rendering its wall of "Let me check the
   * query…" prose forever, with no way to shift it short of a regeneration.
   * Cleaning here makes every entry render clean, old ones included.
   */
  const dashboardHtml = useMemo(
    () => (summary ? extractMarkup(summary.html) : ""),
    [summary],
  );

  /*
    Demo mode: the briefing is about the signed-in user's own park and visit
    history, neither of which exists against the shared demo dataset. Say so
    rather than render the "no machines available" empty state, which would read
    as a fault.
  */
  if (demoMode) {
    return (
      <div className="space-y-4">
        <h2 className={cx("text-xl font-semibold", themeClasses.textPrimary)}>
          {title}
        </h2>
        <p className={cx("text-sm", themeClasses.textSecondary)}>
          {t("whatsNew.demoDisabled", {
            defaultValue:
              "The daily briefing is turned off in demo mode. Switch demo mode off in Settings to get briefings about your own machines again.",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h2
              className={cx("text-xl font-semibold", themeClasses.textPrimary)}
            >
              {title}
            </h2>
            {/*
              Provenance marker. The body is an LLM summarising production data,
              and a line manager or director could act on the figures, so it has
              to be obvious where they came from.
            */}
            <span
              className="px-2 py-0.5 rounded-full text-xs font-semibold border border-violet-400/40 bg-violet-500/10 text-violet-600 dark:text-violet-300"
              title={t("whatsNew.aiTooltip", {
                defaultValue:
                  "Written by an AI assistant from your production data. It can be incomplete or wrong — check the machine views before acting on it.",
              })}
            >
              {t("whatsNew.aiBadge", { defaultValue: "AI generated" })}
            </span>
          </div>
          {subtitle && (
            <p className={cx("text-sm mt-0.5", themeClasses.textSecondary)}>
              {subtitle}
            </p>
          )}
        </div>

        <button
          onClick={regenerate}
          disabled={busy}
          className={cx(
            "shrink-0 px-3 py-1.5 rounded text-sm font-medium transition-colors",
            busy
              ? "bg-gray-400 text-white cursor-not-allowed"
              : "bg-brand text-on-brand hover:bg-brand-bright",
          )}
        >
          {busy
            ? t("whatsNew.working", { defaultValue: "Working…" })
            : t("whatsNew.refresh", { defaultValue: "Refresh" })}
        </button>
      </div>

      {/*
        Machines the briefing refers to. Rendered out here on purpose: the
        briefing HTML lives in a sandboxed iframe that cannot navigate the app,
        so links have to exist outside it to be clickable.
      */}
      {summary && summary.machines.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className={cx("text-xs", themeClasses.textTertiary)}>
            {t("whatsNew.mentioned", { defaultValue: "Mentioned:" })}
          </span>
          {summary.machines.map((m) => (
            <Link
              key={m.id}
              to={`/machine/${encodeURIComponent(m.id)}`}
              className={cx(
                "px-2.5 py-1 rounded-full text-xs font-medium border transition-colors",
                themeClasses.bgSecondary,
                themeClasses.borderPrimary,
                themeClasses.textSecondary,
                "hover:bg-black/5 dark:hover:bg-white/10",
              )}
              title={m.label ? `${m.id} — ${m.label}` : m.id}
            >
              {m.label ? `${m.id} · ${m.label}` : m.id}
            </Link>
          ))}
        </div>
      )}

      <div
        className={cx(
          "rounded-xl border p-4",
          themeClasses.bgSecondary,
          themeClasses.borderPrimary,
        )}
      >
        {status === "generating" && !summary ? (
          <Skeleton />
        ) : summary ? (
          <SandboxedHtmlFrame html={dashboardHtml} title={title} />
        ) : status === "error" ? (
          <div className="text-center py-8">
            <p className={cx("text-sm font-medium mb-1", "text-red-500")}>
              {t("whatsNew.failed", {
                defaultValue: "Couldn't build your briefing",
              })}
            </p>
            {error && (
              <p className={cx("text-xs", themeClasses.textTertiary)}>
                {error}
              </p>
            )}
          </div>
        ) : status === "empty" ? (
          <p
            className={cx(
              "text-sm text-center py-8",
              themeClasses.textSecondary,
            )}
          >
            {t("whatsNew.noMachines", {
              defaultValue: "No machines are available for your account yet.",
            })}
          </p>
        ) : (
          <Skeleton />
        )}
      </div>

      {/*
        Standing disclaimer, shown whenever there's a briefing on screen.
        Deliberately not conditional on anything: the figures are LLM-derived and
        the reader has no other signal that they're unverified.
      */}
      {summary && (
        <p className={cx("text-xs leading-relaxed", themeClasses.textTertiary)}>
          {t("whatsNew.aiDisclaimer", {
            defaultValue:
              "This summary is written by an AI assistant from your production data. It may be incomplete, may miss something important, or may state a figure incorrectly — treat it as a starting point and confirm anything that matters in the machine or line views.",
          })}
        </p>
      )}

      {/* A stale briefing is kept on screen when a refresh fails — say so. */}
      {error && summary && (
        <p className={cx("text-xs", themeClasses.textTertiary)}>
          {t("whatsNew.staleNotice", {
            defaultValue: "Showing your previous briefing — the update failed.",
          })}
        </p>
      )}
    </div>
  );
};

export default WelcomeTab;
